import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Upload,
  CheckCircle2,
  ShieldCheck,
  Info,
  Send,
  FileText,
} from "lucide-react";

// --- THEME CONSTANTS (Moved to global scope to fix undefined errors) ---
const THEME = {
  colors: {
    primary: "#dc2626", // Red
    primaryDark: "#b91c1c",
    accent: "#D4AF37", // Gold
    bg: "#09090b", // Dark
    textMuted: "#71717a", // Gray
    border: "#e4e4e7", // Zinc 200
  },
};

export default function RFQ() {
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate API Submission
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 1500);
  };

  if (submitted) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "2rem",
          backgroundColor: "#f9f9fb",
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          style={{
            maxWidth: "500px",
            textAlign: "center",
            backgroundColor: "white",
            padding: "3rem",
            borderRadius: "8px",
            boxShadow: "0 10px 25px rgba(0,0,0,0.05)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginBottom: "1.5rem",
            }}
          >
            <CheckCircle2 size={64} color={THEME.colors.primary} />
          </div>
          <h2
            style={{
              fontSize: "2rem",
              fontWeight: 900,
              marginBottom: "1rem",
              textTransform: "uppercase",
            }}
          >
            RFQ Submitted
          </h2>
          <p
            style={{
              color: THEME.colors.textMuted,
              lineHeight: 1.6,
              marginBottom: "2rem",
            }}
          >
            Thank you for your interest in FOKAL. Your technical requirements
            have been received. Our engineering and commercial team will review
            the specifications and contact you shortly.
          </p>
          <button
            onClick={() => setSubmitted(false)}
            style={{
              padding: "1rem 2rem",
              backgroundColor: "#000",
              color: "#fff",
              border: "none",
              fontWeight: "bold",
              cursor: "pointer",
              borderRadius: "2px",
            }}
          >
            Submit Another Request
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "6rem 1rem",
        backgroundColor: "#f4f4f5",
        fontFamily: "Inter, system-ui, sans-serif",
      }}
    >
      <div style={{ maxWidth: "900px", margin: "0 auto" }}>
        {/* Header Section */}
        <header style={{ marginBottom: "3rem", textAlign: "center" }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "10px",
              marginBottom: "1rem",
            }}
          >
            <div
              style={{
                width: "30px",
                height: "2px",
                backgroundColor: THEME.colors.accent,
              }}
            ></div>
            <span
              style={{
                color: THEME.colors.accent,
                fontWeight: 800,
                textTransform: "uppercase",
                fontSize: "12px",
                letterSpacing: "0.1em",
              }}
            >
              Technical Procurement
            </span>
            <div
              style={{
                width: "30px",
                height: "2px",
                backgroundColor: THEME.colors.accent,
              }}
            ></div>
          </div>
          <h1
            style={{
              fontSize: "clamp(2rem, 5vw, 3rem)",
              fontWeight: 900,
              textTransform: "uppercase",
              margin: 0,
              color: "#09090b",
            }}
          >
            Request For Quotation
          </h1>
          <p
            style={{
              color: THEME.colors.textMuted,
              marginTop: "1rem",
              maxWidth: "600px",
              margin: "1rem auto 0",
            }}
          >
            Provide your technical specifications below. Our team provides
            indigenized and custom lighting solutions for Automotive, Defence,
            and Aerospace applications.
          </p>
        </header>

        <form
          onSubmit={handleSubmit}
          style={{ display: "flex", flexDirection: "column", gap: "2rem" }}
        >
          {/* SECTION A: COMPANY DETAILS */}
          <FormCard
            title="Section A: Company Details"
            icon={<Info size={18} />}
          >
            <div style={gridStyle}>
              <FormInput
                label="Company Name *"
                required
                placeholder="Enter organization name"
              />
              <FormInput
                label="Contact Person *"
                required
                placeholder="Your full name"
              />
              <FormInput
                label="Email *"
                type="email"
                required
                placeholder="work@company.com"
              />
              <FormInput
                label="Phone / WhatsApp *"
                required
                placeholder="+91 00000 00000"
              />
              <FormInput
                label="Country *"
                required
                placeholder="e.g. India, Germany"
              />
              <div style={inputGroupStyle}>
                <label style={labelStyle}>Company Type *</label>
                <select required style={selectStyle}>
                  <option value="">Select Option</option>
                  <option>OEM</option>
                  <option>Defence / Govt</option>
                  <option>Distributor</option>
                  <option>Trader</option>
                  <option>Other</option>
                </select>
              </div>
            </div>
          </FormCard>

          {/* SECTION B: REQUIREMENT DETAILS */}
          <FormCard
            title="Section B: Requirement Details"
            icon={<FileText size={18} />}
          >
            <div style={gridStyle}>
              <div style={inputGroupStyle}>
                <label style={labelStyle}>Application *</label>
                <select required style={selectStyle}>
                  <option value="">Select Application</option>
                  <option>Automotive</option>
                  <option>Defence / Aerospace</option>
                  <option>Airfield / Infrastructure</option>
                </select>
              </div>
              <div style={inputGroupStyle}>
                <label style={labelStyle}>Product Category *</label>
                <select required style={selectStyle}>
                  <option value="">Select Category</option>
                  <option>Signal / Stop Lamps</option>
                  <option>Wedge / Miniature Lamps</option>
                  <option>Headlamp / Halogen</option>
                  <option>Defence-Grade Lamps</option>
                  <option>Custom / Development</option>
                </select>
              </div>
              <FormInput label="Voltage" placeholder="e.g. 12V / 24V / 28V" />
              <FormInput label="Wattage" placeholder="e.g. 5W / 21W / 70W" />
              <FormInput
                label="Base Type"
                placeholder="e.g. BA15s, P43t, Wedge"
              />
              <FormInput
                label="Annual Volume Estimate"
                placeholder="Number of units"
              />
              <div
                style={{
                  ...inputGroupStyle,
                  gridColumn: isSubmitting ? "auto" : "span 1",
                }}
              >
                <label style={labelStyle}>Target Market</label>
                <select style={selectStyle}>
                  <option>India (Domestic)</option>
                  <option>Export</option>
                  <option>Defence (Direct)</option>
                </select>
              </div>
            </div>
          </FormCard>

          {/* SECTION C: COMPLIANCE */}
          <FormCard
            title="Section C: Compliance"
            icon={<ShieldCheck size={18} />}
          >
            <p
              style={{
                fontSize: "13px",
                color: THEME.colors.textMuted,
                marginBottom: "1.5rem",
              }}
            >
              Indicate required certifications or standards for this specific
              requirement.
            </p>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "2rem",
                marginBottom: "1.5rem",
              }}
            >
              <label style={checkboxLabelStyle}>
                <input type="checkbox" style={checkboxStyle} /> ISO / IATF 16949
              </label>
              <label style={checkboxLabelStyle}>
                <input type="checkbox" style={checkboxStyle} /> MIL-STD
                Compliance
              </label>
              <label style={checkboxLabelStyle}>
                <input type="checkbox" style={checkboxStyle} /> ECE / AIS
                Approved
              </label>
            </div>
            <FormInput
              label="Other Regulatory Requirements"
              placeholder="List any other specific standards"
            />
          </FormCard>

          {/* SECTION D: ADDITIONAL INFORMATION */}
          <FormCard
            title="Section D: Additional Information"
            icon={<Upload size={18} />}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "1.5rem",
              }}
            >
              <div style={uploadZoneStyle}>
                <Upload
                  size={32}
                  color={THEME.colors.textMuted}
                  style={{ marginBottom: "10px" }}
                />
                <p style={{ margin: 0, fontWeight: 600 }}>
                  Upload Drawing / Spec Sheet
                </p>
                <p
                  style={{
                    margin: "4px 0 0",
                    fontSize: "12px",
                    color: THEME.colors.textMuted,
                  }}
                >
                  PDF, DXF, JPG or PNG (Max 10MB)
                </p>
                <input type="file" style={{ display: "none" }} />
              </div>
              <div style={inputGroupStyle}>
                <label style={labelStyle}>Message / Technical Notes</label>
                <textarea
                  rows={4}
                  placeholder="Tell us more about your specific project requirements..."
                  style={textareaStyle}
                ></textarea>
              </div>
            </div>
          </FormCard>

          {/* Submit Button Area */}
          <div style={{ textAlign: "center", marginTop: "1rem" }}>
            <motion.button
              whileHover={{
                scale: 1.02,
                backgroundColor: THEME.colors.primaryDark,
              }}
              whileTap={{ scale: 0.98 }}
              disabled={isSubmitting}
              type="submit"
              style={{
                width: "100%",
                padding: "1.25rem",
                backgroundColor: THEME.colors.primary,
                color: "white",
                border: "none",
                fontSize: "1.1rem",
                fontWeight: 900,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                cursor: "pointer",
                borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "12px",
                boxShadow: "0 10px 15px -3px rgba(220, 38, 38, 0.3)",
              }}
            >
              {isSubmitting ? (
                "Processing..."
              ) : (
                <>
                  Submit RFQ <Send size={18} />
                </>
              )}
            </motion.button>
            <div
              style={{
                marginTop: "1.5rem",
                color: THEME.colors.textMuted,
                fontSize: "13px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              <ShieldCheck size={16} />
              All enquiries are reviewed by our technical & commercial team.
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// --- SUB-COMPONENTS & STYLES ---

const FormCard = ({ title, icon, children }) => (
  <div
    style={{
      backgroundColor: "white",
      padding: "2.5rem",
      borderRadius: "4px",
      border: "1px solid #e4e4e7",
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        marginBottom: "2rem",
      }}
    >
      <div style={{ color: THEME.colors.primary }}>{icon}</div>
      <h3
        style={{
          fontSize: "14px",
          fontWeight: 800,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          margin: 0,
        }}
      >
        {title}
      </h3>
    </div>
    {children}
  </div>
);

const FormInput = ({ label, ...props }) => (
  <div style={inputGroupStyle}>
    <label style={labelStyle}>{label}</label>
    <input {...props} style={inputStyle} />
  </div>
);

const gridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
  gap: "24px",
};

const inputGroupStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
};

const labelStyle = {
  fontSize: "12px",
  fontWeight: 700,
  textTransform: "uppercase",
  color: "#3f3f46",
  letterSpacing: "0.02em",
};

const inputStyle = {
  padding: "12px 16px",
  border: "1px solid #e4e4e7",
  borderRadius: "2px",
  fontSize: "14px",
  backgroundColor: "#f9f9fb",
  outline: "none",
  transition: "border-color 0.2s",
  ":focus": {
    borderColor: THEME.colors.primary,
  },
};

const selectStyle = {
  ...inputStyle,
  cursor: "pointer",
  appearance: "none",
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
  backgroundRepeat: "no-repeat",
  backgroundPosition: "right 12px center",
  backgroundSize: "16px",
};

const textareaStyle = {
  ...inputStyle,
  resize: "vertical",
  minHeight: "100px",
};

const checkboxLabelStyle = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  fontSize: "14px",
  fontWeight: 600,
  cursor: "pointer",
  color: "#18181b",
};

const checkboxStyle = {
  width: "18px",
  height: "18px",
  accentColor: THEME.colors.primary,
  cursor: "pointer",
};

const uploadZoneStyle = {
  border: "2px dashed #e4e4e7",
  padding: "2.5rem",
  textAlign: "center",
  borderRadius: "4px",
  backgroundColor: "#f9f9fb",
  cursor: "pointer",
  transition: "all 0.2s",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
};
